import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

function LogisticPlanningTable({ rows = [] }) {
  const navigate = useNavigate();

  // ---------- state ----------
  const [show, setShow] = useState(false);
  const [active, setActive] = useState(null);

  // ---------- helpers ----------
  const badge = (text, variant = "secondary") => (
    <span className={`badge bg-${variant}`}>{text}</span>
  );

  const num = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  const fmtQty = (v) =>
    v !== null && v !== undefined && !isNaN(Number(v)) ? Number(v).toFixed(3) : "-";

  const packingCell = (r) => r?.packing_style ?? r?.packing_type ?? "-";

  const isLogisticDone = (r) => {
    const txt = (r?.logistic_status ?? "").toString().trim().toLowerCase();
    if (txt === "done") return true;
    if (txt === "pending") return false;
    const vehiclesPlanned = num(r?.vehicles_planned);
    const logisticPlannedQty = num(r?.logistic_planned_qty);
    const hasVehiclesArray = Array.isArray(r?.vehicles) && r.vehicles.length > 0;
    return vehiclesPlanned > 0 || logisticPlannedQty > 0 || hasVehiclesArray;
  };

  const renderLogisticStatus = (r) =>
    isLogisticDone(r) ? badge("Done", "success") : badge("Pending", "danger");

  const goToVehiclePlan = (row) => {
    const id = row?.planning_id ?? row?.plan_id ?? row?.id;
    if (!id) {
      alert("Planning ID not found on this row.");
      return;
    }
    navigate(`/logistics/plan/${id}`, { state: { plan: row } });
  };

  const openPreview = (row) => {
    setActive(row || null);
    setShow(true);
  };
  const closePreview = () => setShow(false);

  // ✅ pick a plan-level vehicle planning date (plan field first, then from vehicles[])
  const getPlanVehiclePlannedOn = (row) => {
    if (!row) return null;
    const direct =
      row.logistic_planned_on ??
      row.vehicle_planned_on ??
      row.dispatch_date;
    if (direct) return direct;

    if (Array.isArray(row.vehicles) && row.vehicles.length) {
      const candidates = row.vehicles
        .map((v) => v.dispatch_date ?? v.planned_on ?? v.vehicle_planned_on ?? v.created_on)
        .filter(Boolean)
        .sort();
      return candidates[0] ?? null;
    }
    return null;
  };

  // ---------- derived ----------
  const hasRows = Array.isArray(rows) && rows.length > 0;

  const vehicleRows = useMemo(() => {
    const v = active?.vehicles;
    return Array.isArray(v) ? v : [];
  }, [active]);

  return (
    <>
      <div className="table-responsive mt-3">
        {!hasRows ? (
          <div className="alert alert-info mt-3 mb-0">
            No planning rows found for the selected filters.
          </div>
        ) : (
          <table className="table table-sm table-bordered align-middle text-wrap">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Material</th>
                <th>Packing</th>
                <th className="text-end">Planned Qty</th>
                <th>Delivery</th>
                <th>Destination</th>
                {/* ✅ NEW: Priority column */}
                <th>Priority</th>
                <th className="text-end">Vehicles Planned</th>
                <th className="text-end">Logistic Planned Qty</th>
                <th>Logistic</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const vehicles = num(r?.vehicles_planned);
                const lqty = num(r?.logistic_planned_qty);
                const done = isLogisticDone(r);

                return (
                  <tr key={r.id ?? r.plan_id ?? `${r.sales_order_id || "row"}_${i}`}>
                    <td>{i + 1}</td>
                    <td>{r.customer_name ?? r.customer ?? "-"}</td>
                    <td>{r.material_name ?? "-"}</td>
                    <td>{packingCell(r)}</td>
                    <td className="text-end">{fmtQty(r.planned_qty)}</td>
                    <td>{r.delivery_date ?? "-"}</td>
                    <td>{r.destination_point ?? r.destination ?? "-"}</td>
                    {/* ✅ NEW: show priority */}
                    <td>{r.priority ?? "-"}</td>
                    <td className="text-end">{vehicles || "-"}</td>
                    <td className="text-end">{lqty ? lqty.toFixed(3) : "-"}</td>
                    <td>{renderLogisticStatus(r)}</td>
                    <td className="text-center d-flex gap-2 justify-content-center">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => openPreview(r)}
                        title="Quick preview"
                      >
                        👁️ Preview
                      </button>

                      {!done ? (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => goToVehiclePlan(r)}
                        >
                          🚚 Plan Vehicles
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => goToVehiclePlan(r)}
                          title="Review / Update"
                        >
                          🔍 Review
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ---------- Preview Modal ---------- */}
      <div
        className={`modal fade ${show ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "transparent" }}
        onClick={closePreview}
        onKeyDown={(e) => e.key === "Escape" && closePreview()}
      >
        <div
          className="modal-dialog modal-xl modal-dialog-scrollable"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Plan Preview
                {active?.planning_id || active?.plan_id || active?.id
                  ? ` #${active?.planning_id ?? active?.plan_id ?? active?.id}`
                  : ""}
              </h5>
              <button type="button" className="btn-close" onClick={closePreview} />
            </div>

            <div className="modal-body">
              {!active ? (
                <div className="text-muted">No data.</div>
              ) : (
                <>
                  {/* ✅ Three cards: Basic | Logistics | Destination information */}
                  <div className="row g-3">
                    {/* Basic */}
                    <div className="col-md-4">
                      <div className="border rounded p-2 h-100">
                        <h6 className="mb-2">Basic</h6>
                        <div><strong>Customer:</strong> {active.customer_name ?? active.customer ?? "-"}</div>
                        <div><strong>Material:</strong> {active.material_name ?? "-"}</div>
                        <div><strong>Packing:</strong> {packingCell(active)}</div>
                        <div><strong>Planned Qty:</strong> {fmtQty(active.planned_qty)}</div>
                        <div><strong>Delivery:</strong> {active.delivery_date ?? "-"}</div>
                        {/* Destination & Priority moved to Destination information card */}
                      </div>
                    </div>

                    {/* Logistics */}
                    <div className="col-md-4">
                      <div className="border rounded p-2 h-100">
                        <h6 className="mb-2">Logistics</h6>
                        <div><strong>Status:</strong> {renderLogisticStatus(active)}</div>
                        <div><strong>Vehicles Planned:</strong> {num(active.vehicles_planned) || "-"}</div>
                        <div>
                          <strong>Logistic Planned Qty:</strong>{" "}
                          {num(active.logistic_planned_qty) ? num(active.logistic_planned_qty).toFixed(3) : "-"}
                        </div>
                        <div>
                          <strong>Vehicle Planned On:</strong>{" "}
                          {getPlanVehiclePlannedOn(active) ?? "-"}
                        </div>
                        <div>
                          <strong>Invoice Date:</strong>{" "}
                          {active.invoice_date ?? "-"}
                        </div>
                        <div><strong>Remark:</strong> {active.remark ?? active.so_remark ?? "-"}</div>
                      </div>
                    </div>

                    {/* ✅ Destination information */}
                    <div className="col-md-4">
                      <div className="border rounded p-2 h-100">
                        <h6 className="mb-2">Destination information</h6>
                        <div>
                          <strong>Destination:</strong>{""}
                          {active.destination_point ?? active.destination ?? "-"}
                        </div>
                        <div>
                          <strong>Priority:</strong>{" "}
                          {active.priority ?? "-"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="my-3" />

                  <h6 className="mb-2">Vehicles</h6>
                  {vehicleRows.length === 0 ? (
                    <div className="text-muted">No vehicles planned yet.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered align-middle text-wrap">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Vehicle Planned Date</th>
                            <th>Vehicle No</th>
                            <th>Driver</th>
                            <th>Mobile</th>
                            <th>Planned Qty</th>
                            <th>Invoiced Qty</th>
                            <th>Dispatch / Planned On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicleRows.map((v, i) => (
                            <tr key={v.id ?? i}>
                              <td>{i + 1}</td>
                              <td>{getPlanVehiclePlannedOn(active) ?? "-"}</td>
                              <td>{v.vehicle_no ?? "-"}</td>
                              <td>{v.driver_name ?? "-"}</td>
                              <td>{v.driver_mobile ?? "-"}</td>
                              <td className="text-end">
                                {fmtQty(
                                  v.vehicle_planned_qty ??
                                    v.logistic_planned_qty ??
                                    v.planned_qty ??
                                    v.qty ??
                                    v.dispatch_qty
                                )}
                              </td>
                              <td className="text-end">
                                {fmtQty(v.vehicle_invoice_qty ?? v.invoice_qty)}
                              </td>
                              <td>{v.dispatch_date ?? v.planned_on ?? v.vehicle_planned_on ?? v.created_on ?? "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              {active && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    closePreview();
                    goToVehiclePlan(active);
                  }}
                >
                  {isLogisticDone(active) ? "Review / Update" : "Plan Vehicles"}
                </button>
              )}
              <button className="btn btn-outline-secondary" onClick={closePreview}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LogisticPlanningTable;
